// frontend/src/pages/Contact/Contact.jsx
import React, { useState, useEffect } from 'react';
import emailjs from 'emailjs-com';
import { useTranslation } from 'react-i18next';
import contactHeroImg from '../Contact/img/contact-bg.webp';
import { ensureNamespace } from '../../i18n/ensurePageI18n';

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_placeholder';
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_placeholder';
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'public_key_placeholder';

// 使用共用 helper 建立同步載入資源的函式
const loadI18n = (i18n) => {
  ensureNamespace(i18n, 'contact', {
    en: {
      sectionTitle: 'Contact',
      sectionSubtitle: 'Global Support • Rapid Response',
      title: 'CONTACT US',
      subtitle: 'Reach out for sales, technical support, or general inquiries.',
      taiwanTitle: 'Taiwan Office',
      winTrissTaiwan: 'Wintriss Taiwan',
      address: 'No. 1009, Ln. 401, Sec. 7, Chengde Rd., Beitou Dist., Taipei City 112, Taiwan',
      officePhone: 'Office',
      fax: 'Fax',
      skype: 'Skype Chat',
      formTitle: 'Inquiry Form',
      name: 'Name',
      company: 'Company',
      email: 'Email',
      mail: 'Email',
      phone: 'Phone',
      country: 'Country',
      message: 'Message / Requirements',
      send: 'Send',
      sending: 'Sending...',
      success: 'Sent successfully. We will reply soon.',
      error: 'Failed to send. Please retry later.',
      required: 'Required',
      labelNote: 'All * fields required',
      captchaHint: 'Leave blank (anti-bot)'
    },
    zh: {
      sectionTitle: '聯絡我們',
      sectionSubtitle: '全球支援・快速回覆',
      title: '聯絡我們',
      subtitle: '歡迎洽詢銷售、技術支援或一般問題。',
      taiwanTitle: '台灣辦公室',
      winTrissTaiwan: 'Wintriss Taiwan',
      address: '臺灣臺北市北投區承德路七段401巷1009號',
      officePhone: '電話',
      fax: '傳真',
      skype: 'Skype 線上洽談',
      formTitle: '詢問表單',
      name: '姓名',
      company: '公司',
      email: 'Email', // 與英文一致，避免 mail / email 混用
      mail: 'Email', // 保留 mail key 兼容現有程式碼
      phone: '電話',
      country: '國家',
      message: '訊息／需求說明',
      send: '送出',
      sending: '寄送中...',
      success: '寄送成功，我們將盡快回覆。',
      error: '寄送失敗，請稍後再試。',
      required: '必填',
      labelNote: '標示 * 為必填欄位',
      captchaHint: '請保持空白（防機器人）'
    }
  });
};

const ContactPage = () => {
  const { t, i18n } = useTranslation();
  // 同步載入資源，避免首 render 顯示 key
  loadI18n(i18n);

  const [formStatus, setFormStatus] = useState('');
  const [sending, setSending] = useState(false);
  const [flashVisible, setFlashVisible] = useState(false);

  // 初始化 emailjs（只需一次）
  useEffect(() => {
    try { emailjs.init(PUBLIC_KEY); } catch (_) {}
  }, []);

  // 狀態提示自動淡出
  useEffect(() => {
    if (flashVisible) {
      const timer = setTimeout(() => setFlashVisible(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [flashVisible]);

  const showStatus = (msg) => {
    setFormStatus(msg);
    setFlashVisible(true);
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    if (sending) return;
    // 簡單 bot 防護：隱藏欄位若有值則直接當失敗
    if (e.target.extra_field.value) {
      showStatus(t('contact:error'));
      return;
    }
    // 前端基礎驗證（Email 與姓名必填）
    const name = e.target.user_name.value.trim();
    const email = e.target.user_email.value.trim();
    if (!name || !email) {
      showStatus(t('contact:error'));
      return;
    }
    setSending(true);
    showStatus(t('contact:sending'));
    try {
      await emailjs.sendForm(SERVICE_ID, TEMPLATE_ID, e.target);
      showStatus(t('contact:success'));
      e.target.reset();
    } catch (err) {
      showStatus(t('contact:error'));
    } finally {
      setSending(false);
    }
  };

  const statusType =
    formStatus === t('contact:success')
      ? 'success'
      : formStatus === t('contact:error')
        ? 'error'
        : formStatus === t('contact:sending')
          ? 'sending'
          : '';

  const feedbackStyle = {
    margin: '.25rem 0 0',
    padding: '.75rem 1rem',
    borderRadius: '8px',
    fontSize: '.8rem',
    fontWeight: 500,
    lineHeight: 1.4,
    display: formStatus ? 'block' : 'none',
    opacity: flashVisible ? 1 : 0,
    transform: flashVisible ? 'translateY(0)' : 'translateY(-6px)',
    transition: 'opacity .4s ease, transform .4s ease',
    background:
      statusType === 'success'
        ? '#d1e7dd'
        : statusType === 'error'
          ? '#f8d7da'
          : statusType === 'sending'
            ? '#e2e3e5'
            : '#f1f1f1',
    color:
      statusType === 'success'
        ? '#0f5132'
        : statusType === 'error'
          ? '#842029'
          : statusType === 'sending'
            ? '#41464b'
            : '#333',
    border:
      statusType === 'success'
        ? '1px solid #badbcc'
        : statusType === 'error'
          ? '1px solid #f5c2c7'
          : statusType === 'sending'
            ? '1px solid #d3d6d8'
            : '1px solid #ddd',
    boxShadow:
      statusType === 'success'
        ? '0 0 0 3px rgba(25,135,84,.15)'
        : statusType === 'error'
          ? '0 0 0 3px rgba(220,53,69,.15)'
          : 'none'
  };

  return (
    <div>
      <section className="contact-hero relative text-white py-20">
        <div className="absolute inset-0 bg-black bg-opacity-70" />
        <div className="absolute inset-0">
          <img
            src={contactHeroImg}
            alt={t('contact:sectionTitle')}
            style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.35 }}
            loading="lazy"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <h1 className="text-5xl font-bold mb-4">{t('contact:sectionTitle')}</h1>
          <p className="text-xl text-white">{t('contact:sectionSubtitle')}</p>
        </div>
      </section>

      <main
        style={{
          padding: 'clamp(1rem,2vw,2.5rem)',
          maxWidth: '1200px',
          margin: '0 auto',
          fontFamily: 'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif',
          lineHeight: 1.6
        }}
      >
        <header style={{ marginBottom: '1.5rem' }}>
          <h2
            style={{
              fontSize: 'clamp(1.8rem,4vw,2.6rem)',
              margin: 0,
              letterSpacing: '.5px'
            }}
          >
            {t('contact:title')}
          </h2>
            <p style={{ margin: '.5rem 0 0', color: '#555' }}>{t('contact:subtitle')}</p>
        </header>

        <section
          style={{
            display: 'grid',
            gap: '2rem',
            gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%, 320px),1fr))', // slightly wider min for better balance
            alignItems: 'start',
            marginBottom: '2.5rem'
          }}
        >
          {/* Taiwan Office */}
          <div
            style={{
              padding: '1rem',
              background: '#ffffff',
              border: '1px solid #e2e6ea',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '.5rem'
            }}
          >
            <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 600 }}>
              {t('contact:taiwanTitle')}
            </h3>
            <strong>{t('contact:winTrissTaiwan')}</strong>
            <p style={{ margin: 0 }}>{t('contact:address')}</p>
            <p style={{ margin: 0 }}>
              <strong>{t('contact:officePhone')}:</strong> 886-2-2892-8000
            </p>
            <p style={{ margin: 0 }}>
              <strong>{t('contact:fax')}:</strong> 886-2-2892-5000
            </p>
            <p style={{ margin: 0 }}>
              <strong>{t('contact:mail')}:</strong> sales@iwscan.com
            </p>
          </div>

          {/* Contact Form */}
          <form
            onSubmit={sendEmail}
            style={{
              padding: '1rem',
              background: '#ffffff',
              border: '1px solid #e2e6ea',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '.9rem',
              position: 'relative'
            }}
          >
            <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 600 }}>
              {t('contact:formTitle')}
            </h3>
            <small style={{ color: '#777' }}>{t('contact:labelNote')}</small>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '.35rem' }}>
              <label htmlFor="user_name">{t('contact:name')} *</label>
              <input
                id="user_name"
                name="user_name"
                type="text"
                required
                placeholder={t('contact:name')}
                style={fieldStyle}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '.35rem' }}>
              <label htmlFor="company">{t('contact:company')}</label>
              <input
                id="company"
                name="company"
                type="text"
                placeholder={t('contact:company')}
                style={fieldStyle}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '.35rem' }}>
              <label htmlFor="user_email">{t('contact:email')} *</label>
              <input
                id="user_email"
                name="user_email"
                type="email"
                required
                placeholder="name@example.com"
                style={fieldStyle}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '.35rem' }}>
              <label htmlFor="phone">{t('contact:phone')}</label>
              <input
                id="phone"
                name="phone"
                type="tel"
                placeholder="+886 ..."
                style={fieldStyle}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '.35rem' }}>
              <label htmlFor="country">{t('contact:country')}</label>
              <input
                id="country"
                name="country"
                type="text"
                placeholder={t('contact:country')}
                style={fieldStyle}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '.35rem' }}>
              <label htmlFor="message">{t('contact:message')}</label>
              <textarea
                id="message"
                name="message"
                rows={5}
                placeholder={t('contact:message')}
                style={{ ...fieldStyle, resize: 'vertical' }}
              />
            </div>

            <div style={{ display: 'none' }}>
              <label htmlFor="extra_field">{t('contact:captchaHint')}</label>
              <input id="extra_field" name="extra_field" type="text" />
            </div>

            <button
              type="submit"
              disabled={sending}
              style={{
                padding: '.75rem 1.1rem',
                background:
                  statusType === 'success'
                    ? '#198754'
                    : statusType === 'error'
                      ? '#dc3545'
                      : sending
                        ? '#6c757d'
                        : '#0d6efd',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: sending ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                letterSpacing: '.5px',
                fontSize: '.85rem',
                transition: 'background .3s ease, transform .25s ease',
                transform:
                  statusType === 'success'
                    ? 'scale(1.02)'
                    : statusType === 'error'
                      ? 'scale(1.02)'
                      : 'scale(1)'
              }}
            >
              {sending ? t('contact:sending') : t('contact:send')}
            </button>

            <div aria-live="polite" role="alert" style={feedbackStyle}>
              {formStatus}
            </div>
          </form>
        </section>
      </main>
    </div>
  );
};

const fieldStyle = {
  width: '100%',
  padding: '.55rem .7rem',
  border: '1px solid #ced4da',
  borderRadius: '6px',
  fontSize: '.8rem',
  fontFamily: 'inherit',
  background: '#fff'
};

export default ContactPage;
